import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../services/api';
import Logo from '../components/common/Logo';

/**
 * Pantalla d'entrada.
 *
 * Dues portes. La de sempre és el compte amb contrasenya: durant el curs cada
 * alumne en té un, autoritzat per l'instructor, i és per aquí que entra a fer les
 * pràctiques. Per això surt seleccionada.
 *
 * El codi imprès és l'excepció: el dia de l'examen, o quan cal entrar de pressa
 * sense passar per l'alta i l'aprovació. Es canvia amb el valor inicial de `porta`.
 *
 * L'entrada amb codi va en dos passos a posta. El segon desplaça la sessió que hi
 * hagi oberta, i això no pot passar mentre algú encara pot prémer «cancel·la»: el
 * grup que estigui fent l'examen a l'altre ordinador quedaria fora per una simple
 * comprovació.
 */
function LoginPage() {
  const navigate = useNavigate();
  const { login, loginAmbCodi, register, isAuthenticated, loading } = useAuth();

  const [porta, setPorta] = useState('compte'); // 'compte' | 'codi'
  const [isRegister, setIsRegister] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', firstName: '', lastName: '' });

  // Entrada amb codi
  const [codi, setCodi] = useState('');
  const [comprovat, setComprovat] = useState(null); // { displayName, sessioOberta }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      if (isRegister) {
        const result = await register(formData);
        if (result.requiresApproval) {
          navigate('/pending-approval');
          return;
        }
      } else {
        await login({ email: formData.email, password: formData.password });
        navigate('/dashboard');
      }
    } catch (err) {
      if (err.response?.data?.code === 'PENDING_APPROVAL') {
        navigate('/pending-approval');
        return;
      }
      setError(err.response?.data?.message || 'S\'ha produït un error');
    } finally {
      setSubmitting(false);
    }
  };

  const comprovaCodi = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const { data } = await auth.comprovaCodi(codi);
      setComprovat(data);
    } catch (err) {
      setError(err.response?.data?.message || 'No s\'ha pogut comprovar el codi');
    } finally {
      setSubmitting(false);
    }
  };

  const entra = async () => {
    setSubmitting(true);
    setError('');
    try {
      const data = await loginAmbCodi(codi);
      // El servidor decideix on aterra: si hi ha un exercici a mitges, hi torna.
      navigate(data.desti || '/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'No s\'ha pogut entrar');
      setComprovat(null);
    } finally {
      setSubmitting(false);
    }
  };

  const canviaPorta = (nova) => {
    setPorta(nova);
    setError('');
    setComprovat(null);
  };

  const pestanya = (id, etiqueta) => (
    <button
      type="button"
      onClick={() => canviaPorta(id)}
      className={`flex-1 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors duration-fast ${
        porta === id
          ? 'border-accent text-accent'
          : 'border-transparent text-content-muted hover:text-content-secondary'
      }`}
    >
      {etiqueta}
    </button>
  );

  return (
    <div className="min-h-screen bg-surface-base flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center mb-4">
            <Logo className="w-16 h-16 text-accent" />
          </div>
          <h1 className="text-center text-3xl font-bold text-content tracking-wide">ARGOS</h1>
          <p className="mt-2 text-center text-sm text-content-secondary">
            Anàlisi d'amenaces i operacions de seguretat
          </p>
        </div>

        <div className="card p-0 overflow-hidden">
          <div className="flex border-b border-border">
            {pestanya('compte', 'Compte i contrasenya')}
            {pestanya('codi', "Codi d'examen")}
          </div>

          <div className="p-5">
            {error && (
              <div className="mb-4 bg-error-subtle border border-error/30 text-error-text px-4 py-3 rounded">
                {error}
              </div>
            )}

            {porta === 'codi' && !comprovat && (
              <form onSubmit={comprovaCodi} className="space-y-4">
                <div>
                  <label htmlFor="codi" className="block text-sm font-medium text-content-secondary mb-1">
                    Codi de la targeta
                  </label>
                  <input
                    id="codi"
                    value={codi}
                    onChange={(e) => { setCodi(e.target.value); setError(''); }}
                    placeholder="GR07-K7DM-P4XT"
                    autoComplete="off"
                    autoCapitalize="characters"
                    spellCheck="false"
                    required
                    className="input font-mono text-lg tracking-widest text-center uppercase"
                  />
                  <p className="mt-2 text-xs text-content-muted">
                    Tal com surt a la targeta. Els guionets, les majúscules i els
                    espais són indiferents.
                  </p>
                </div>
                <button type="submit" disabled={submitting || !codi.trim()}
                        className="w-full btn btn-primary py-3 disabled:opacity-50">
                  {submitting ? 'Comprovant...' : 'Continuar'}
                </button>
              </form>
            )}

            {porta === 'codi' && comprovat && (
              <div className="space-y-4 text-center">
                <p className="text-sm text-content-secondary">Aquest codi és de</p>
                <p className="text-2xl font-semibold text-content">{comprovat.displayName}</p>

                {comprovat.sessioOberta && (
                  <div className="bg-warning-subtle border border-warning/30 text-warning-text px-4 py-3 rounded text-sm text-left">
                    Aquest grup ja té una sessió oberta en un altre ordinador. Si
                    entres aquí, l'altra es tancarà i haurà de tornar a posar el
                    codi. El treball no es perd.
                  </div>
                )}

                <div className="flex gap-3">
                  <button type="button" onClick={() => { setComprovat(null); setError(''); }}
                          className="flex-1 btn btn-secondary py-3">
                    Cancel·lar
                  </button>
                  <button type="button" onClick={entra} disabled={submitting}
                          className="flex-1 btn btn-primary py-3 disabled:opacity-50">
                    {submitting ? 'Entrant...' : 'Entrar'}
                  </button>
                </div>
              </div>
            )}

            {porta === 'compte' && (
              <form onSubmit={handleSubmit} className="space-y-4">
                {isRegister && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-content-secondary mb-1">Nom</label>
                      <input id="firstName" name="firstName" type="text"
                             value={formData.firstName} onChange={handleChange} className="input" />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-content-secondary mb-1">Cognoms</label>
                      <input id="lastName" name="lastName" type="text"
                             value={formData.lastName} onChange={handleChange} className="input" />
                    </div>
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-content-secondary mb-1">Correu electrònic</label>
                  <input id="email" name="email" type="email" required autoComplete="username"
                         value={formData.email} onChange={handleChange} className="input" />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-content-secondary mb-1">Contrasenya</label>
                  <input id="password" name="password" type="password" required minLength={8}
                         autoComplete={isRegister ? 'new-password' : 'current-password'}
                         value={formData.password} onChange={handleChange} className="input" />
                </div>

                <button type="submit" disabled={submitting}
                        className="w-full btn btn-primary py-3 disabled:opacity-50">
                  {submitting ? 'Espereu...' : (isRegister ? 'Crear compte' : 'Iniciar sessió')}
                </button>

                <div className="text-center">
                  <button type="button" onClick={() => { setIsRegister(!isRegister); setError(''); }}
                          className="text-sm link">
                    {isRegister ? 'Ja tens un compte? Inicia sessió' : 'No tens compte? Registra\'t'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
